/**
 * HNS Round Card Component
 *
 * Round card for both Hide and Seek game types (Hide and Seek, Hide and Seek
 * Teams — whose players are shown by team, with the round's hiding team) with:
 * - Role display (hiders/seekers)
 * - DQ status and badges
 * - Marco Polo integration
 * - Role toggle functionality
 * - Disqualify and revoke DQ
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Round,
  Player,
  isHideSeekSettings,
  isHideSeekRoundPlayer,
  isHnstRoundPlayer,
  isRoundActive,
  HnsRoundSettings,
  HnstRoundSettings,
  HNST_TEAM_KEYS,
  HNST_TEAMS,
  hnstCoinTossLabel,
  otherHnstTeam,
  resolveHnstHidingTeams,
  canAdminDo,
  GameType,
  RoundEventKind,
  isRulesetChangePayload,
  HNS_RULE_KEYS,
  formatCameraDistance,
  getHnstRoundScoring,
  getPlayerDisplayName,
  hideSeekPublicDisplayKeys,
  resolveHideSeekPublicDisplay,
  HIDE_SEEK_PUBLIC_DISPLAY_LABELS,
  type Game,
  type GameStatus,
  type HnstHidingTeamSource,
  type HnsRoleRules,
  type HnsRuleAllowance,
  type RulesetChangePayload,
} from '@swtorbit/shared-socket-events';
import { formatDuration, formatHnstRoundScoring, getRoundStatusColor, useRoundTimer, useServerClockOffset } from '@swtorbit/shared-react-hooks';
import { useGameContext } from '../../contexts/GameContext';
import { useHnsContext } from '../../contexts/HnsContext';
import { RoundCardBase, RoundActionsBar } from '../round';
import { HnsPlayerItem } from './HnsPlayerItem';
import { MarcoPoloSection } from './MarcoPoloSection';
import { RoundEventsTimeline } from './RoundEventsTimeline';
import { CameraCheckSection } from './CameraCheckSection';
import { HnsRoundMap, HnstTeamName } from '@swtorbit/shared-components';
import { Modal } from '@swtorbit/ui/react';
import { usePlayerLocations } from '@swtorbit/shared-react-hooks';

const SEEKER_VISIBILITY_SHORT = { everyone: 'everyone', seekers: 'seekers', admins: 'admins only' } as const;

/** How a rule reads on the card: ✗ off, ∞ unlimited, or the number of times allowed. */
function allowanceSymbol(allowance: HnsRuleAllowance): string {
  if (allowance === 'off') return '✗';
  if (allowance === 'unlimited') return '∞';
  return `${allowance}×`;
}

/** One role's rules as they were set for the round. */
function RoleRulesSummary({ title, rules }: { title: string; rules: HnsRoleRules }) {
  return (
    <div className="role-rules-col">
      <strong>{title}:</strong>
      {HNS_RULE_KEYS.map(({ key, label }) => (
        <span key={key} className={rules[key] === 'off' ? 'text-red' : 'text-green'}>
          {label}: {allowanceSymbol(rules[key])}
        </span>
      ))}
    </div>
  );
}

/** Hide and Seek Teams: who hides in the round, and what decides it. */
function HnstHidingLine({ game, index }: { game: Game; index: number }) {
  const serverOffsetMs = useServerClockOffset();
  const hiding = resolveHnstHidingTeams(game, Date.now() + serverOffsetMs)[index];
  if (!hiding) return null;
  const toss = hiding.coinTossId !== null ? hnstCoinTossLabel(game, hiding.coinTossId) : null;
  const why: Record<HnstHidingTeamSource, string> = {
    played: '',
    pick: ' — their Team Captain pick',
    fixed: ' — set by you',
    toss: ` — ${toss}`,
    turn: ' — turn order',
  };
  if (hiding.team) {
    return <span><HnstTeamName team={hiding.team} /> hide{why[hiding.source]}{hiding.source === 'pick' && toss ? `, ${toss} decides when` : ''}</span>;
  }
  return (
    <span>
      <strong>Hiding team:</strong>{' '}
      {hiding.source === 'toss' ? `${toss} decides` : 'not decided — set a team, link a coin toss, or play the round before it'}
    </span>
  );
}

/**
 * Type-safe helper to get HNS settings from a round.
 */
function getHnsSettings(round: Round): HnsRoundSettings | HnstRoundSettings | undefined {
  return isHideSeekSettings(round.settings) ? round.settings : undefined;
}

/** Hide and Seek Teams: the seconds until a counting-down round's hiding begins, by the server's clock. */
function CountdownTimerDisplay({ endsAt }: { endsAt: number }) {
  const serverOffsetMs = useServerClockOffset();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.ceil((endsAt - (now + serverOffsetMs)) / 1000));
  return (
    <div className="timer timer--lg timer--boxed timer--soft timer--aqua">
      <span className="timer__label">STARTING IN</span>
      <span className="timer__value">{seconds}</span>
    </div>
  );
}

// Admin Timer Display Component for HNS
function HnsTimerDisplay({ round }: { round: Round }) {
  const { timerState, displayTime, maxDisplayTime, isUnlimited, isActive, phase } = useRoundTimer(round);

  if (round.status === 'countdown' && round.countdownEndsAt) {
    return <CountdownTimerDisplay endsAt={round.countdownEndsAt} />;
  }

  if (!isActive || !timerState) {
    return null;
  }

  // Hiding countdown phase
  if (phase === 'hiding') {
    return (
      <div className="timer timer--lg timer--boxed timer--soft timer--amber">
        <span className="timer__label">HIDING COUNTDOWN</span>
        <span className="timer__value">{displayTime}</span>
      </div>
    );
  }

  // Active round timer phase (seeking)
  return (
    <div className="timer timer--lg">
      <span className="timer__label">ROUND TIME</span>
      <span className="timer__value">
        {displayTime}
        {!isUnlimited && <span className="timer__max"> / {maxDisplayTime}</span>}
      </span>
    </div>
  );
}

export interface HnsRoundCardProps {
  round: Round;
  index: number;
  hasActiveRound: boolean;
  isLoaded: boolean;
  gamePlayers: Player[];
  onStart: (index: number) => void;
  onEnd: (index: number) => void;
  onCancel: (index: number) => void;
  onDisable: (index: number) => void;
  onDelete: (index: number) => void;
  onEdit: (index: number) => void;
  onLoad: (index: number) => void;
  onUnload: (index: number) => void;
  onTogglePlayerRole: (roundIndex: number, playerId: string, currentRole: 'hider' | 'seeker') => void;
  onForceResults: (index: number) => void;
}

export const HnsRoundCard = memo(function HnsRoundCard({
  round,
  index,
  hasActiveRound,
  isLoaded,
  gamePlayers,
  onStart,
  onEnd,
  onCancel,
  onDisable,
  onDelete,
  onEdit,
  onLoad,
  onUnload,
  onTogglePlayerRole,
  onForceResults,
}: HnsRoundCardProps) {
  const { selectedGame } = useGameContext();
  const {
    marcoPoloEvents,
    activateMarcoPolo,
    cancelMarcoPolo,
    roundEvents,
    scheduleRoundEvent,
    deleteRoundEvent,
    applyRoundEventNow,
    setCameraCheck,
    disqualifyPlayer,
    revokeDisqualification,
  } = useHnsContext();
  const [playerActionError, setPlayerActionError] = useState<string | null>(null);

  const isActive = isRoundActive(round);

  // Live seeker (and, for admins, hider) positions while the round runs.
  const liveLocations = usePlayerLocations({ enabled: isActive, roomCode: selectedGame?.roomCode ?? null });
  const locationList = useMemo(() => Object.values(liveLocations), [liveLocations]);
  const latestMarcoPoloAt = useMemo(
    () => (marcoPoloEvents.length ? Math.max(...marcoPoloEvents.map((e) => e.activatedAt)) : null),
    [marcoPoloEvents],
  );
  // Ages in the map's list tick while the round runs.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, [isActive]);

  // Only surface Marco Polo events on active rounds
  const visibleMarcoPoloEvents = useMemo(() => (
    isActive ? marcoPoloEvents : []
  ), [isActive, marcoPoloEvents]);

  // Only surface Round Events on active rounds
  const visibleRoundEvents = useMemo(() => (
    isActive ? roundEvents : []
  ), [isActive, roundEvents]);

  // Compute the currently-effective ruleset for this round from fired events
  // so the "Copy from Current" / "Change Rules Now" prefill matches server state.
  const currentEffective = useMemo(() => {
    const settings = isHideSeekSettings(round.settings) ? round.settings : undefined;
    const base = {
      hiderRules: settings?.hiderRules,
      seekerRules: settings?.seekerRules,
    };
    const firedRulesetChanges = roundEvents
      .filter(e =>
        e.kind === RoundEventKind.RULESET_CHANGE &&
        e.status !== 'cancelled' &&
        (e.firedAt ?? 0) > 0 &&
        isRulesetChangePayload(e.payload)
      )
      .sort((a, b) => (a.firedAt ?? 0) - (b.firedAt ?? 0));
    const last = firedRulesetChanges[firedRulesetChanges.length - 1];
    if (last && isRulesetChangePayload(last.payload)) {
      return { hiderRules: last.payload.hiderRules, seekerRules: last.payload.seekerRules };
    }
    return base;
  }, [round.settings, roundEvents]);

  const handleActivateMarcoPolo = useCallback(() => {
    if (!selectedGame) return;
    activateMarcoPolo({ roomCode: selectedGame.roomCode });
  }, [selectedGame, activateMarcoPolo]);

  const handleCancelMarcoPolo = useCallback((eventId: string) => {
    if (!selectedGame) return;
    cancelMarcoPolo({ roomCode: selectedGame.roomCode, eventId });
  }, [selectedGame, cancelMarcoPolo]);

  const handleSetCameraCheck = useCallback((roundIndex: number, playerId: string, checked: boolean) => {
    if (!selectedGame) return;
    setCameraCheck({ roomCode: selectedGame.roomCode, roundIndex, playerId, checked });
  }, [selectedGame, setCameraCheck]);

  const handleDisqualify = useCallback(async (roundIndex: number, playerId: string) => {
    if (!selectedGame) return;
    const name = getPlayerDisplayName(playerId, gamePlayers);
    // Cancel keeps them in; the reason (optional) is what the player is shown.
    const reason = window.prompt(`Disqualify ${name} from round ${roundIndex + 1}? Reason shown to them (optional):`);
    if (reason === null) return;
    setPlayerActionError(null);
    const error = await disqualifyPlayer({ roomCode: selectedGame.roomCode, roundIndex, playerId, reason: reason.trim() });
    if (error) setPlayerActionError(`Couldn't disqualify ${name}: ${error}`);
  }, [selectedGame, gamePlayers, disqualifyPlayer]);

  const handleRevokeDisqualification = useCallback(async (roundIndex: number, playerId: string) => {
    if (!selectedGame) return;
    const name = getPlayerDisplayName(playerId, gamePlayers);
    const ended = round.status === 'completed';
    if (!window.confirm(`Revoke ${name}'s disqualification in round ${roundIndex + 1}?${ended ? ' The round is scored again.' : ''}`)) return;
    setPlayerActionError(null);
    const error = await revokeDisqualification({ roomCode: selectedGame.roomCode, roundIndex, playerId });
    if (error) setPlayerActionError(`Couldn't revoke ${name}'s disqualification: ${error}`);
  }, [selectedGame, gamePlayers, round.status, revokeDisqualification]);

  const settings = getHnsSettings(round);

  if (!settings) {
    return <div className="card card--sm card--bg-midnight card--border card--border-navy">Invalid HNS round settings</div>;
  }

  // The rules in effect now: each player's uses are shown against them.
  const effectiveRules: RulesetChangePayload = {
    hiderRules: currentEffective.hiderRules ?? settings.hiderRules,
    seekerRules: currentEffective.seekerRules ?? settings.seekerRules,
  };

  // Derive game-lifecycle-aware button visibility
  const gameStatus = selectedGame?.status as GameStatus | undefined;
  const gameType = selectedGame?.gameType ?? GameType.HIDE_AND_SEEK;
  const isTeams = settings.gameType === GameType.HIDE_AND_SEEK_TEAMS;
  const canLoad = gameStatus !== undefined
    && canAdminDo(gameType, gameStatus, 'canLoadRound')
    && round.status === 'setup'
    && !isLoaded;
  const canUnloadOrStart = gameStatus !== undefined
    && canAdminDo(gameType, gameStatus, 'canLoadRound')
    && round.status === 'setup'
    && isLoaded;
  const canEnd = isActive;
  const countingDown = round.status === 'countdown';
  const canDelete = round.status === 'setup' && !isLoaded;
  const canEdit = round.status === 'setup' && !isLoaded;
  const canDisable = round.status === 'completed';

  return (
    <RoundCardBase
      index={index}
      status={round.status}
      disabled={round.disabled}
      borderColor={getRoundStatusColor(round.status)}
      timer={isActive && <HnsTimerDisplay round={round} />}
      actions={
        <RoundActionsBar
          leftActions={
            <>
              {canDelete && (
                <button onClick={() => onDelete(index)} className="btn btn--red">
                  🗑️ Delete
                </button>
              )}
              {canEdit && (
                <button onClick={() => onEdit(index)} className="btn btn--aqua">
                  ✏️ Edit
                </button>
              )}
              {canDisable && (
                <button onClick={() => onDisable(index)} className={`btn ${round.disabled ? 'btn--green' : 'btn--amber'}`}>
                  {round.disabled ? '✅ Enable' : '🚫 Disable'}
                </button>
              )}
              {round.status === 'completed' && (
                <button
                  onClick={() => onForceResults(index)}
                  className={`btn ${round.forceShowResults ? 'btn--green' : 'btn--outline btn--slate'}`}
                  title={round.forceShowResults ? 'Click to stop forcing results display' : 'Click to force results display in overlays'}
                >
                  {round.forceShowResults ? '📊 Results Shown' : '📊 Force Results'}
                </button>
              )}
            </>
          }
          rightActions={
            <>
              {canLoad && (
                <button onClick={() => onLoad(index)} className="btn btn--aqua">
                  📥 Load
                </button>
              )}
              {canUnloadOrStart && (
                <button onClick={() => onUnload(index)} className="btn btn--outline btn--slate">
                  📤 Unload
                </button>
              )}
              {canUnloadOrStart && !hasActiveRound && (
                <button onClick={() => onStart(index)} className="btn btn--green">
                  ▶️ Start
                </button>
              )}
              {canUnloadOrStart && hasActiveRound && (
                <button disabled className="btn btn--ghost" title="Another round is active">
                  ▶️ Start (Blocked)
                </button>
              )}
              {canEnd && (
                <>
                  {/* A round counting down can only be cancelled: it hasn't started. */}
                  {!countingDown && <button onClick={() => onEnd(index)} className="btn">✅ End</button>}
                  <button onClick={() => onCancel(index)} className="btn btn--red">❌ Cancel</button>
                  {!countingDown && <button onClick={handleActivateMarcoPolo} className="btn btn--ghost btn-marco-polo">📍 Marco Polo</button>}
                </>
              )}
            </>
          }
        />
      }
      footer={visibleMarcoPoloEvents.length > 0 && (
        <MarcoPoloSection
          events={visibleMarcoPoloEvents}
          gamePlayers={gamePlayers}
          onCancel={handleCancelMarcoPolo}
        />
      )}
    >
      <div className="d-flex gap-sm flex-wrap">
        {round.status === 'setup' && (settings.usesGameDefaults
          ? <span className="badge badge--sm badge--aqua" title="Follows the game's round defaults until it starts">⚙️ Game defaults</span>
          : <span className="badge badge--sm badge--amber" title="Keeps its own settings when the round defaults change">✏️ Custom</span>)}
        <span><strong>Hiding:</strong> {formatDuration(settings.hidingDuration)}</span>
        <span><strong>Round:</strong> {formatDuration(settings.roundDuration)}</span>
        <span>
          <strong>Location:</strong>{' '}
          {round.hnsLocation
            ? `${round.hnsLocation.zone?.name ? `${round.hnsLocation.areaName} · ${round.hnsLocation.zone.name}` : round.hnsLocation.areaName}${round.hnsLocation.source === 'detected' ? ' (detected)' : ''}`
            : 'detected at start'}
        </span>
        <span><strong>Marco Polo:</strong> ±{settings.marcoPoloRadius} m</span>
        {settings.maxCameraDistance != null && (
          <span><strong>Max camera:</strong> {formatCameraDistance(settings.maxCameraDistance)}</span>
        )}
        {'seekerVisibility' in settings && (
          <span><strong>Seekers shown to:</strong> {SEEKER_VISIBILITY_SHORT[settings.seekerVisibility]}</span>
        )}
        {'hidingTeam' in settings && (
          <span><strong>Scoring:</strong> {formatHnstRoundScoring(getHnstRoundScoring(settings))}</span>
        )}
        {'hidingTeam' in settings && selectedGame && <HnstHidingLine game={selectedGame} index={index} />}
        {(() => {
          const shown = resolveHideSeekPublicDisplay(settings.publicDisplay);
          const hidden = hideSeekPublicDisplayKeys(settings.gameType).filter((key) => !shown[key]);
          return hidden.length > 0 ? (
            <span><strong>Hidden on rules page:</strong> {hidden.map((key) => HIDE_SEEK_PUBLIC_DISPLAY_LABELS[key]).join(', ')}</span>
          ) : null;
        })()}
      </div>
      {'ownerTeam' in settings && settings.ownerTeam && (
        <p className="fs-sm mt-xs mb-0">
          <span className={`badge badge--${HNST_TEAMS[settings.ownerTeam].hue}`}>{HNST_TEAMS[settings.ownerTeam].name}&apos; pick</span>{' '}
          {round.status !== 'setup' || settings.revealedAt
            ? 'Revealed to everyone.'
            : <>Hidden from the <HnstTeamName team={otherHnstTeam(settings.ownerTeam)} /> and the public until you load it.</>}
        </p>
      )}
      {settings.publicNotes && <p className="fs-sm text-slate mt-xs mb-0"><strong>Note for players:</strong> {settings.publicNotes}</p>}

      {round.hnsLocation && (isActive || round.status === 'completed') && (
        <div className="mt-xs">
          <HnsRoundMap
            round={round}
            location={round.hnsLocation}
            players={gamePlayers}
            locations={isActive ? locationList : []}
            showExact
            interactive
            Modal={Modal}
            flagOutsideArea
            latestMarcoPoloAt={latestMarcoPoloAt}
            now={now}
          />
        </div>
      )}

      {/* Role-specific rules display */}
      <div className="grid grid--1-2 gap-sm fs-sm">
        <RoleRulesSummary title="Hider Rules" rules={settings.hiderRules} />
        <RoleRulesSummary title="Seeker Rules" rules={settings.seekerRules} />
      </div>

      <p className="fw-bold mt-xs">Players{round.status === 'completed' ? ' (Scores)' : ''}:</p>
      {playerActionError && <div className="alert alert--red alert--sm">{playerActionError}</div>}
      {isTeams ? (
        round.players.length === 0 ? (
          <p className="text-slate fs-sm">The teams&apos; connected members play, from when the round starts.</p>
        ) : (
          <div className="grid grid--1-2 gap-sm">
            {HNST_TEAM_KEYS.map((team) => (
              <div key={team} className="d-flex flex-column gap-2xs">
                <HnstTeamName team={team} />
                {round.players.filter(isHnstRoundPlayer).filter((p) => p.team === team).map((player) => (
                  <HnsPlayerItem
                    key={player.playerId}
                    player={player}
                    round={round}
                    roundIndex={index}
                    roundStatus={round.status}
                    gamePlayers={gamePlayers}
                    onToggleRole={onTogglePlayerRole}
                    onDisqualify={handleDisqualify}
                    onRevokeDisqualification={handleRevokeDisqualification}
                    outLabel={player.eliminatedByDeaths ? 'ELIMINATED (DEATHS)' : 'OUT'}
                    rules={effectiveRules}
                  />
                ))}
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="d-flex flex-column gap-2xs">
          {round.players.filter(isHideSeekRoundPlayer).map(player => (
            <HnsPlayerItem
              key={player.playerId}
              player={player}
              round={round}
              roundIndex={index}
              roundStatus={round.status}
              gamePlayers={gamePlayers}
              onToggleRole={onTogglePlayerRole}
              onDisqualify={handleDisqualify}
              onRevokeDisqualification={handleRevokeDisqualification}
              rules={effectiveRules}
            />
          ))}
        </div>
      )}

      {selectedGame && (
        <CameraCheckSection game={selectedGame} round={round} roundIndex={index} onSetCheck={handleSetCameraCheck} />
      )}

      {isActive && selectedGame && (
        <RoundEventsTimeline
          mode="live"
          events={visibleRoundEvents}
          baselineHiderRules={currentEffective.hiderRules ?? settings.hiderRules}
          baselineSeekerRules={currentEffective.seekerRules ?? settings.seekerRules}
          onSchedule={(draft) => scheduleRoundEvent({
            roomCode: selectedGame.roomCode,
            roundIndex: index,
            kind: draft.kind,
            scheduledOffsetSeconds: draft.scheduledOffsetSeconds,
            leadTimeSeconds: draft.leadTimeSeconds,
            payload: draft.payload,
          })}
          onDelete={(eventId) => deleteRoundEvent({
            roomCode: selectedGame.roomCode,
            eventId,
          })}
          onApplyNow={(payload) => applyRoundEventNow({
            roomCode: selectedGame.roomCode,
            roundIndex: index,
            payload,
          })}
        />
      )}
    </RoundCardBase>
  );
});
